import React, { useContext, useEffect, useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import { AppsButtonContainer } from '../../component/button/appsButtonContainer';
import './home.scss';
import { useParams, useNavigate } from 'react-router-dom'; // Import hooks for routing
import mobileMockup from '../../assets/home/mobile_mockup.png';
import iphoneHandMockup from '../../assets/home/mockup_iphone_hand.jpg';
import { translateText } from '../../services/deeplTranslationService';
import { LanguageContext } from '../../services/LanguageContext'; // Import LanguageContext

// HeroSection Component
const HeroSection = () => {
  const { lang } = useParams(); // Grab the 'lang' parameter from the URL
  const { language } = useContext(LanguageContext); // Get the selected language from context
  const [heroTitle, setHeroTitle] = useState('Finden Sie Ihren perfekten Partner'); // Default in German

  useEffect(() => {

    console.log("Current language from URL, home.js :", lang);

    // Only translate if language is not German
    if (language !== 'DE') {
      const translateTexts = async () => {
        const translatedHeroTitle = await translateText('Find Your Perfect Partner', language);
        setHeroTitle(translatedHeroTitle);
      };
      translateTexts();
    }
  }, [language, lang]);



  return (
    <div className="hero-section">
      <div className="hero-overlay d-flex flex-column align-items-center justify-content-center">
        <h2 className='text-white'>{heroTitle}</h2>
        <span className='hero-text-margin'> </span>
        <AppsButtonContainer />
      </div>
    </div>
  );
};

// Features Component
const Features = () => {
  const { language } = useContext(LanguageContext); // Get language from context
  const [translatedFeatures, setTranslatedFeatures] = useState([
    { title: 'Einfache Vermittlung', description: 'Finden Sie Ihren perfekten Partner mit unserem intelligenten Matching-Algorithmus.', icon: 'bi-heart' },
    { title: 'Verifizierte Profile', description: 'Verbinden Sie sich mit echten und verifizierten Mitgliedern.', icon: 'bi-shield-check' },
    { title: 'Kostenlos', description: 'Genießen Sie alle Funktionen kostenlos mit minimaler Werbung!', icon: 'bi-gift' }
  ]); // Default content in German

  useEffect(() => {
    // Only translate if language is not German
    if (language !== 'DE') {
      const translateFeatures = async () => {
        const newFeatures = await Promise.all(
          translatedFeatures.map(async (feature) => {
            const translatedTitle = await translateText(feature.title, language);
            const translatedDescription = await translateText(feature.description, language);
            return { ...feature, title: translatedTitle, description: translatedDescription };
          })
        );
        setTranslatedFeatures(newFeatures);
      };
      translateFeatures();
    }
  }, [language]);

  return (
    <div className="container features section-margin">
      <h2 className="text-center my-4">{language === 'EN' ? 'Features' : 'Funktionen'}</h2>
      <Row>
        {translatedFeatures.map((feature, index) => (
          <Col key={index} sm={12} md={4} className="feature-col">
            <div className="home-card feature">
              <div className="feature-img-container">
                <i className={`bi ${feature.icon} feature-icon`}></i> {/* Use Bootstrap icons */}
              </div>
              <h3 className="feature-title">{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
          </Col>
        ))}
      </Row>
    </div>
  );
};

// AppHighlight Component
const AppHighlightSection = () => {
  const { language } = useContext(LanguageContext); // Get language from context
  const [highlightTitle, setHighlightTitle] = useState('Entdecken Sie die Zukunft des Datings.'); // Default in German
  const [highlightDescription, setHighlightDescription] = useState('Unsere mobile App wurde entwickelt, um die Welt des Datings zu Ihren Fingerspitzen zu bringen. Verbinden Sie sich mit echten, verifizierten Profilen, chatten Sie sicher und erleben Sie intelligentes Matching wie nie zuvor.'); // Default in German

  useEffect(() => {
    // Only translate if language is not German
    if (language !== 'DE') {
      
      const translateAppHighlight = async () => {
        const translatedTitle = await translateText('Discover the Future of Dating.', language);
        const translatedDescription = await translateText('Our mobile app is designed to bring the dating world to your fingertips. Connect with real, verified profiles, chat securely, and experience intelligent matching like never before.', language);
        setHighlightTitle(translatedTitle);
        setHighlightDescription(translatedDescription);
      };

      translateAppHighlight();
    }
  }, [language]);

  return (
    <div className="app-highlight featurette text-center">
      <div className='container section-margin'>
        <div className="row align-items-center">
          <div className="col-md-5">
            <img src={mobileMockup} alt="Mobile App" className="featurette-image img-fluid mx-auto" />
          </div>
          <div className="col-md-7 p-5">
            <h2 className="featurette-heading">{highlightTitle}</h2>
            <p className="lead">{highlightDescription}</p>
            <div className="download-buttons">
              <AppsButtonContainer />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Home Component
const Home = () => {
  return (
    <>
      <HeroSection />
      <div className="container">
        <Features />
      </div>
      <AppHighlightSection />
      <PrototypeSection />

    </>
  );
};

// PrototypeSection Component
const PrototypeSection = () => {
  const { language } = useContext(LanguageContext); // Get language from context

  // Default text in German
  const defaultText = {
    features: [
      { title: 'Anpassbare Profileinstellungen', description: 'Passen Sie Ihr Profil und Suchkriterien nach Belieben an.' },
      { title: 'Social Login-Optionen', description: 'Integrieren Sie beliebige Social-Media-Logins.' },
      { title: 'Eingeschränkte Einladungen', description: 'Senden Sie exklusive Einladungen an ausgewählte Benutzer.' },
      { title: 'Chat und Bildfreigabe', description: 'Chatten Sie sicher und teilen Sie Bilder in Echtzeit.' },
      { title: 'Suche nach verschiedenen Kriterien', description: 'Verwenden Sie erweiterte Filter, um den perfekten Partner zu finden.' },
      { title: 'Freunde hinzufügen', description: 'Fügen Sie neue Freunde hinzu und erweitern Sie Ihr Netzwerk.' },
    ],
  };

  // State for translated text
  const [translatedText, setTranslatedText] = useState(defaultText);

  useEffect(() => {
    // Translate text if language is not German
    if (language !== 'DE') {
      const translateContent = async () => {

        const translatedFeatures = await Promise.all(
          defaultText.features.map(async (feature) => {
            const translatedTitle = await translateText(feature.title, language);
            const translatedDescription = await translateText(feature.description, language);
            return { title: translatedTitle, description: translatedDescription };
          })
        );

        setTranslatedText({
          features: translatedFeatures,
        });
      };

      translateContent();
    }
  }, [language]);

  return (
    <section id="alt-features" className="alt-features section section-margin">
      <div className='container'>
        <div className="row">
          <div className="col-xl-7 d-flex order-2 order-xl-1" data-aos="fade-up" data-aos-delay="200">
            <div className="row align-self-center gy-5 p-5">
              {/* Dynamically render each feature */}
              {translatedText.features.map((feature, index) => (
                <div className="col-md-6 icon-box" key={index}>
                  <i className={`bi ${index === 0 ? 'bi-sliders' : index === 1 ? 'bi-shield-lock' : index === 2 ? 'bi-person-check' : index === 3 ? 'bi-chat-dots' : index === 4 ? 'bi-search' : 'bi-person-plus'}`}></i>
                  <div>
                    <h4>{feature.title}</h4>
                    <p>{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="col-xl-5 d-flex align-items-center order-1 order-xl-2" data-aos="fade-up" data-aos-delay="100">
            <img src={iphoneHandMockup} className="img-fluid prototype-img " alt="App Mockup" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Home;
